import React, { useState } from 'react';
import {
  Box, Container, Paper, Title, Text, Group,
  ThemeIcon, Button, TextInput, NumberInput, Select,
  Textarea, FileInput, SimpleGrid, Divider,
  Notification, Badge
} from '@mantine/core';
import {
  IconReceipt2,
  IconArrowLeft,
  IconUpload,
  IconCheck,
  IconX
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axiosConfig';

export default function ExpenseForm() {
  const navigate = useNavigate();

  // Form State
  const [formData, setFormData] = useState({
    expense_date: '',
    expense_time: '',
    category: '',
    amount: '',
    description: '',
    created_by: 'Furkan Atabey',
  });
  const [file, setFile] = useState(null);
  
  // UX State
  const [loading, setLoading] = useState(false);
  const [alertInfo, setAlertInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const showCustomAlert = (type, message) => {
    setAlertInfo({ show: true, type, message });

    if (type === 'success') {
      setTimeout(() => {
        setAlertInfo({ show: false, type: '', message: '' });
      }, 3000); // 3 saniye sonra bildirimi kapat
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Gerekli alanların kontrolü (HTML required prop'a ek olarak)
    if (!formData.expense_date || !formData.expense_time || !formData.category || !formData.amount || !formData.created_by) {
      showCustomAlert('error', 'Lütfen yıldızlı (*) zorunlu alanları doldurun.');
      return;
    }

    setLoading(true);
    
    // FormData nesnesini oluşturuyoruz
    const submitData = new FormData();
    submitData.append('expense_date', formData.expense_date);
    submitData.append('expense_time', formData.expense_time);
    submitData.append('category', formData.category);
    submitData.append('amount', formData.amount);
    submitData.append('created_by', formData.created_by);
    
    // Açıklama varsa ekle, yoksa boş string gönder (backend'de None olması için)
    if (formData.description) {
      submitData.append('description', formData.description);
    } else {
      submitData.append('description', ''); 
    }

    if (file) {
      submitData.append('invoice_file', file);
    }

    try {
      // Axios FormData gönderildiğini anlar ve Content-Type'ı otomatik ayarlar
      await api.post('/istanbul-office/expenses', submitData);

      showCustomAlert('success', 'Gider başarıyla kaydedildi!');
      
      // Başarılı kayıttan sonra formu temizle
      setFormData({
        expense_date: '',
        expense_time: '',
        category: '',
        amount: '',
        description: '',
        created_by: '',
      });
      setFile(null);

    } catch (error) {
      console.error('API Hatası:', error);
      const errorMessage = error.response?.data?.detail || 'Sunucuya bağlanırken bir hata oluştu.';
      showCustomAlert('error', `Kaydetme başarısız: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      style={{
        backgroundColor: '#f8f9fa',
        minHeight: '100vh',
        padding: '40px 24px',
      }}
    >
      <Container size="md">
        
        {/* Üst Bilgi Paneli */}
        <Paper
          radius="xl"
          p="xl"
          mb="xl"
          bg="white"
          style={{
            border: '1px solid #e9ecef',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
          }}
        >
          <Group justify="space-between" align="center">
            <Group gap="lg">
              <ThemeIcon
                size={72}
                radius="xl"
                variant="light"
                color="red"
              >
                <IconReceipt2 size={40} stroke={1.5} />
              </ThemeIcon>

              <div>
                <Badge radius="sm" size="md" variant="light" color="red" mb={6}>İstanbul Ofis</Badge>
                <Title order={2} c="dark.9" fw={800} style={{ letterSpacing: '-0.5px' }}>
                  Yeni Gider İşlemi
                </Title>
                <Text c="dimmed" size="md" mt={4}>
                  Operasyonel giderleri ve faturaları sisteme kaydedin.
                </Text>
              </div>
            </Group>

            <Button 
              variant="subtle" 
              color="gray" 
              leftSection={<IconArrowLeft size={18} />}
              onClick={() => navigate('/istanbul-office/dashboard')} // Panel rotanı kendi yapına göre ayarla
              radius="md"
            >
              Panele Dön
            </Button>
          </Group>
        </Paper>

        {/* Bildirim Alanı */}
        {alertInfo.show && (
          <Notification
            mb="lg"
            radius="md"
            withBorder
            color={alertInfo.type === 'success' ? 'teal' : 'red'}
            icon={alertInfo.type === 'success' ? <IconCheck size="1.1rem" /> : <IconX size="1.1rem" />}
            onClose={() => setAlertInfo({ show: false, type: '', message: '' })}
          >
            {alertInfo.message}
          </Notification>
        )}

        {/* Form Alanı */}
        <Paper
          p={{ base: 'xl', md: 40 }}
          radius="xl"
          bg="white"
          style={{
            border: '1px solid #e9ecef',
            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.03)',
          }}
        >
          <form onSubmit={handleSubmit}>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl">
              <TextInput
                label="Tarih"
                type="date"
                required
                value={formData.expense_date}
                onChange={(e) => handleInputChange('expense_date', e.currentTarget.value)}
                radius="md"
                size="md"
              />
              <TextInput
                label="Saat"
                type="time"
                required
                value={formData.expense_time}
                onChange={(e) => handleInputChange('expense_time', e.currentTarget.value)}
                radius="md"
                size="md"
              />

              <Select
                label="Gider Kalemi"
                placeholder="Seçiniz"
                required
                data={['Akaryakıt', 'Kırtasiye', 'Yemek', 'Konaklama', 'Hırdavat/Malzeme', 'Diğer']}
                value={formData.category}
                onChange={(value) => handleInputChange('category', value)}
                radius="md"
                size="md"
              />

              <NumberInput
                label="Tutar (TL)"
                placeholder="0.00"
                required
                decimalScale={2}
                fixedDecimalScale
                value={formData.amount}
                onChange={(value) => handleInputChange('amount', value)}
                radius="md"
                size="md"
                hideControls
              />
            </SimpleGrid>

            <Divider my="xl" color="#f1f3f5" />

            <SimpleGrid cols={1} spacing="xl">
              <Textarea
                label="Açıklama"
                placeholder="Gider ile ilgili detaylı bilgi giriniz..."
                minRows={3}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.currentTarget.value)}
                radius="md"
                size="md"
              />

              <FileInput
                label="Fatura / Fiş Görüntüsü"
                placeholder="Dosya yüklemek için tıklayın"
                accept="image/png,image/jpeg,application/pdf"
                leftSection={<IconUpload size={20} />}
                value={file}
                onChange={setFile}
                radius="md"
                size="md"
                clearable
              />

              <TextInput
                label="İşleyen Kişi"
                placeholder="Adınız Soyadınız"
                required
                value={formData.created_by}
                onChange={(e) => handleInputChange('created_by', e.currentTarget.value)}
                radius="md"
                size="md"
              />
            </SimpleGrid>

            <Group justify="flex-end" mt={40}>
              <Button 
                variant="default" 
                size="md" 
                radius="md"
                onClick={() => navigate('/office-operations/dashboard')} // Panel rotanı kendi yapına göre ayarla
              >
                İptal
              </Button>
              <Button 
                type="submit" 
                size="md" 
                radius="md" 
                color="red"
                loading={loading}
                leftSection={!loading && <IconCheck size={18} />}
                style={{ transition: 'all 0.2s ease' }}
              >
                Gideri Kaydet
              </Button>
            </Group>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}